import { mergeRecord } from "./dexaRecord.js";

export function toFiniteNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

export function isFiniteNumber(value) {
  return toFiniteNumber(value) !== null;
}

export function round(value, digits = 2) {
  const numeric = toFiniteNumber(value);
  if (numeric === null) return null;
  const factor = 10 ** digits;
  return Math.round((numeric + Number.EPSILON) * factor) / factor;
}

export function ageAtDate(birthDate, examDate) {
  if (!birthDate || !examDate) return null;
  const birth = new Date(`${birthDate}T12:00:00`);
  const exam = new Date(`${examDate}T12:00:00`);
  if (Number.isNaN(birth.getTime()) || Number.isNaN(exam.getTime())) return null;
  let age = exam.getFullYear() - birth.getFullYear();
  const beforeBirthday = exam.getMonth() < birth.getMonth()
    || (exam.getMonth() === birth.getMonth() && exam.getDate() < birth.getDate());
  if (beforeBirthday) age -= 1;
  return age >= 0 ? age : null;
}

function valueOr(value, fallback) {
  return toFiniteNumber(value) ?? fallback;
}

function divide(numerator, denominator) {
  const top = toFiniteNumber(numerator);
  const bottom = toFiniteNumber(denominator);
  return top !== null && bottom !== null && bottom !== 0 ? top / bottom : null;
}

function deriveRegion(region) {
  const result = { ...region };
  result.leanMassG ??= isFiniteNumber(result.leanPlusBmcG)
    && isFiniteNumber(result.bmcG)
    ? Number(result.leanPlusBmcG) - Number(result.bmcG)
    : null;
  result.leanPlusBmcG ??= isFiniteNumber(result.leanMassG)
    && isFiniteNumber(result.bmcG)
    ? Number(result.leanMassG) + Number(result.bmcG)
    : null;
  result.totalMassG ??= isFiniteNumber(result.fatMassG)
    && isFiniteNumber(result.leanPlusBmcG)
    ? Number(result.fatMassG) + Number(result.leanPlusBmcG)
    : null;
  result.fatPercent ??= round(
    divide(
      isFiniteNumber(result.fatMassG) ? Number(result.fatMassG) * 100 : null,
      result.totalMassG,
    ),
    1,
  );
  return result;
}

function sumRegions(regions, field) {
  const values = regions
    .map((region) => toFiniteNumber(region?.[field]))
    .filter((value) => value !== null);
  return values.length === regions.length ? values.reduce((sum, value) => sum + value, 0) : null;
}

function hydrateCompositionBmcFromBone(record) {
  const compositionRegions = record.composition.regions;
  const boneRegions = record.bone.wholeBodyRegions;
  const directRegionMap = {
    leftArm: "leftArm",
    rightArm: "rightArm",
    leftLeg: "leftLeg",
    rightLeg: "rightLeg",
    head: "head",
  };

  for (const [compositionKey, boneKey] of Object.entries(directRegionMap)) {
    compositionRegions[compositionKey].bmcG ??= toFiniteNumber(boneRegions[boneKey]?.bmcG);
  }

  const trunkBoneRegions = ["leftRibs", "rightRibs", "thoracicSpine", "lumbarSpine", "pelvis"]
    .map((key) => boneRegions[key]);
  const trunkBmc = sumRegions(trunkBoneRegions, "bmcG");
  const wholeBodyBmc = toFiniteNumber(record.bone.wholeBody.bmcG);
  const nonTrunkBmc = sumRegions(
    ["leftArm", "rightArm", "leftLeg", "rightLeg", "head"].map((key) => compositionRegions[key]),
    "bmcG",
  );

  compositionRegions.trunk.bmcG ??= trunkBmc
    ?? (wholeBodyBmc !== null && nonTrunkBmc !== null ? wholeBodyBmc - nonTrunkBmc : null);
  record.composition.total.bmcG ??= wholeBodyBmc;
}

export function deriveDexaRecord(input) {
  const record = mergeRecord(input, {});
  hydrateCompositionBmcFromBone(record);
  const regionKeys = ["leftArm", "rightArm", "trunk", "leftLeg", "rightLeg", "head", "android", "gynoid"];
  for (const key of regionKeys) {
    record.composition.regions[key] = deriveRegion(record.composition.regions[key]);
  }
  record.composition.total = deriveRegion(record.composition.total);
  record.composition.subtotal = deriveRegion(record.composition.subtotal);

  const totalRegions = ["leftArm", "rightArm", "trunk", "leftLeg", "rightLeg", "head"]
    .map((key) => record.composition.regions[key]);
  const total = record.composition.total;
  total.fatMassG ??= sumRegions(totalRegions, "fatMassG");
  total.leanMassG ??= sumRegions(totalRegions, "leanMassG");
  total.bmcG ??= sumRegions(totalRegions, "bmcG");
  total.leanPlusBmcG ??= sumRegions(totalRegions, "leanPlusBmcG");
  total.totalMassG ??= sumRegions(totalRegions, "totalMassG");
  total.fatPercent ??= round(divide(
    isFiniteNumber(total.fatMassG) ? Number(total.fatMassG) * 100 : null,
    total.totalMassG,
  ), 1);
  total.leanPercent = round(divide(
    isFiniteNumber(total.leanMassG) ? Number(total.leanMassG) * 100 : null,
    total.totalMassG,
  ), 1);
  total.leanPlusBmcPercent = round(divide(
    isFiniteNumber(total.leanPlusBmcG) ? Number(total.leanPlusBmcG) * 100 : null,
    total.totalMassG,
  ), 1);
  total.bmcPercent = round(divide(
    isFiniteNumber(total.bmcG) ? Number(total.bmcG) * 100 : null,
    total.totalMassG,
  ), 1);
  total.totalMassKg = round(divide(total.totalMassG, 1000), 3);
  total.fatMassKg = round(divide(total.fatMassG, 1000), 3);
  total.leanMassKg = round(divide(total.leanMassG, 1000), 3);
  total.leanPlusBmcKg = round(divide(total.leanPlusBmcG, 1000), 3);
  total.bmcKg = round(divide(total.bmcG, 1000), 3);

  const heightM = divide(record.patient.heightCm, 100);
  const heightSquared = heightM ? heightM ** 2 : null;
  const indices = record.composition.indices;
  indices.bmi ??= round(divide(record.patient.scaleWeightKg, heightSquared), 1);
  indices.fmi ??= round(divide(total.fatMassKg, heightSquared), 2);
  indices.lmi ??= round(divide(total.leanMassKg, heightSquared), 2);
  const appendicularLeanG = sumRegions(
    ["leftArm", "rightArm", "leftLeg", "rightLeg"].map((key) => record.composition.regions[key]),
    "leanMassG",
  );
  indices.appendicularLeanMassKg = round(divide(appendicularLeanG, 1000), 3);
  indices.almi ??= round(divide(indices.appendicularLeanMassKg, heightSquared), 2);

  const android = record.composition.regions.android;
  const gynoid = record.composition.regions.gynoid;
  indices.androidGynoidRatio ??= round(divide(android.fatPercent, gynoid.fatPercent), 2);
  const averageLegFatPercent = (
    valueOr(record.composition.regions.leftLeg.fatPercent, 0)
    + valueOr(record.composition.regions.rightLeg.fatPercent, 0)
  ) / 2;
  indices.trunkLegFatRatio ??= round(
    divide(record.composition.regions.trunk.fatPercent, averageLegFatPercent),
    2,
  );
  const extremityFatG = sumRegions(
    ["leftArm", "rightArm", "leftLeg", "rightLeg"].map((key) => record.composition.regions[key]),
    "fatMassG",
  );
  indices.trunkExtremityFatRatio ??= round(
    divide(record.composition.regions.trunk.fatMassG, extremityFatG),
    2,
  );

  record.patient.age = ageAtDate(record.patient.birthDate, record.exam.date)
    ?? record.patient.age;
  record.derived = {
    heightM: round(heightM, 3),
    appendicularLeanMassKg: indices.appendicularLeanMassKg,
    arms: compareSides(
      record.composition.regions.leftArm,
      record.composition.regions.rightArm,
      "Brazo izquierdo",
      "Brazo derecho",
    ),
    legs: compareSides(
      record.composition.regions.leftLeg,
      record.composition.regions.rightLeg,
      "Pierna izquierda",
      "Pierna derecha",
    ),
  };
  return record;
}

export function compareSides(left, right, leftLabel, rightLabel) {
  const metrics = {};
  for (const field of ["totalMassG", "leanMassG", "fatMassG", "fatPercent"]) {
    const leftValue = toFiniteNumber(left?.[field]);
    const rightValue = toFiniteNumber(right?.[field]);
    if (leftValue === null || rightValue === null) {
      metrics[field] = null;
      continue;
    }
    const difference = leftValue - rightValue;
    const maximum = Math.max(Math.abs(leftValue), Math.abs(rightValue));
    metrics[field] = {
      left: leftValue,
      right: rightValue,
      difference,
      absoluteDifference: Math.abs(difference),
      differencePercent: round(maximum ? Math.abs(difference) * 100 / maximum : 0, 1),
      greaterSide: difference > 0 ? leftLabel : difference < 0 ? rightLabel : "Sin diferencia",
    };
  }
  return metrics;
}
